import React, { useState, useEffect } from 'react';
import { FiSearch, FiPlus, FiEdit2, FiTrash2, FiUser } from 'react-icons/fi';
import AdminLayout from '../../components/admin/AdminLayout';
import adminApiClient from '../../api/adminApiClient';
import config from '../../utils/config';
import { useAdmin } from '../../context/AdminContext';

const AdminManagement = () => {
  const { currentAdmin } = useAdmin();
  const [admins, setAdmins] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [adminsPerPage] = useState(20);
  const [newAdmin, setNewAdmin] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'admin'
  });

  // Fetch admins
  const fetchAdmins = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const endpoint = config.ADMIN_ENDPOINTS.ADMIN_LIST || '/admin/admins';
      const response = await adminApiClient.get(endpoint);
      
      if (response.status === 'success') {
        setAdmins(response.data);
      } else {
        setError('Failed to fetch admins');
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
      setError('An error occurred while fetching admins');
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchAdmins();
  }, []);

  // Handle search
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Filter admins based on search query
  const filteredAdmins = admins.filter(admin => 
    admin.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    admin.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination
  const indexOfLastAdmin = currentPage * adminsPerPage;
  const indexOfFirstAdmin = indexOfLastAdmin - adminsPerPage;
  const currentAdmins = filteredAdmins.slice(indexOfFirstAdmin, indexOfLastAdmin);
  const totalPages = Math.ceil(filteredAdmins.length / adminsPerPage);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Get role badge color
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'superadmin':
        return 'bg-purple-100 text-purple-800';
      case 'admin':
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  // Handle create admin
  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    
    // Validate password length
    if (newAdmin.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    // Validate password match
    if (newAdmin.password !== newAdmin.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      const endpoint = config.ADMIN_ENDPOINTS.CREATE_ADMIN || '/admin/admins';
      const response = await adminApiClient.post(endpoint, {
        name: newAdmin.name,
        email: newAdmin.email,
        password: newAdmin.password,
        role: newAdmin.role
      });
      
      if (response.status === 'success') {
        setShowCreateModal(false);
        setNewAdmin({ name: '', email: '', password: '', confirmPassword: '', role: 'admin' });
        fetchAdmins();
      } else {
        setError('Failed to create admin');
      }
    } catch (error) {
      console.error('Error creating admin:', error);
      setError('An error occurred while creating admin');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete admin
  const handleDeleteAdmin = async () => {

    console.log("Delete button pressed!");
    
    if (!selectedAdmin) return;
    
    // Prevent self-deletion
    if (selectedAdmin._id === currentAdmin._id) {
      setError('You cannot delete your own account');
      setShowDeleteModal(false);
      setSelectedAdmin(null);
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      const endpoint = `${config.ADMIN_ENDPOINTS.ADMIN_LIST || '/admin/admins'}/${selectedAdmin._id}`;
      const response = await adminApiClient.delete(endpoint);

      console.log(response);
      
      if (response.status === 'success') {
        // Update the local state by removing the deleted admin
        setAdmins(prevAdmins => prevAdmins.filter(admin => admin._id !== selectedAdmin._id));
        console.log("Admin Deleted!");
        setShowDeleteModal(false);
        setSelectedAdmin(null);
      } else {
        setError('Failed to delete admin');
      }
    } catch (error) {
      console.error('Error deleting admin:', error);
      setError('An error occurred while deleting admin');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAdmin(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <AdminLayout title="Admin Management">
      <div className="container px-6 py-8 mx-auto max-w-7xl">
        <div className="mb-8 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <h1 className="text-2xl font-bold text-gray-800">Admin Management</h1>
          
          <div>
            {/* Create Admin Button */}
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center px-4 py-2 text-smd btn btn-primary mt-2 mb-2"
            >
              <FiPlus className="mr-2" />
              Create New Admin
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative w-full md:w-64 mt-2 mb-2">
            <input
              type="text"
              placeholder="Search admins..."
              value={searchQuery}
              onChange={handleSearch}
              className="block w-full pl-10 pr-2 py-2 pl-2 text-smd border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:text-sm"
            />
          </div>
          
          
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-md">
            <div className="flex items-center">
              <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* Admin List Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden mt-2">
          <table className="w-full table-fixed divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-1/4">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-1/4">
                  Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-1/4">
                  Role
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-1/4">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                    <p className="mt-3 text-sm text-gray-500">Loading admins...</p>
                  </td>
                </tr>
              ) : currentAdmins.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-10 text-center text-sm text-gray-500">
                    <div className="flex flex-col items-center justify-center">
                      <svg className="h-12 w-12 text-gray-300 mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      No admins found
                    </div>
                  </td>
                </tr>
              ) : (
                currentAdmins.map((admin, index) => (
                  <tr key={admin._id} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="px-6 py-4 whitespace-nowrap" align="center">
                      <div className="text-smd font-medium text-gray-900">{admin.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" align="center">
                      <div className="text-smd text-gray-500">{admin.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" align="center">
                      <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(admin.role)}`}>
                        {admin.role.charAt(0).toUpperCase() + admin.role.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" align="center">
                      <button
                        onClick={() => {
                          setSelectedAdmin(admin);
                          setShowDeleteModal(true);
                        }}
                        disabled={admin._id === currentAdmin._id}
                        className={`text-gray-400 hover:text-red-500 transition-colors duration-200 btn ${
                          admin._id === currentAdmin._id ? 'opacity-50 cursor-not-allowed btn-disabled' : 'btn-secondary'
                        }`}
                      >
                        <FiTrash2 className="h-20 w-20" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-500 order-2 sm:order-1">
              Showing {indexOfFirstAdmin + 1} to {Math.min(indexOfLastAdmin, filteredAdmins.length)} of {filteredAdmins.length} admins
            </div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px order-1 sm:order-2" aria-label="Pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-3 py-1.5 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                  currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => handlePageChange(i + 1)}
                  className={`relative inline-flex items-center px-3 py-1.5 border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === i + 1
                      ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`relative inline-flex items-center px-3 py-1.5 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                  currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </nav>
          </div>
        )}

        {/* Create Admin Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Create New Admin</h3>
              </div>
              <form onSubmit={handleCreateAdmin} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-m font-medium text-gray-700">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={newAdmin.name}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-smd"
                    />
                  </div>
                  <div className="mt-2">
                    <label htmlFor="email" className="block text-m font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={newAdmin.email}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-smd"
                    />
                  </div>
                  <div className="mt-2">
                    <label htmlFor="password" className="block text-m font-medium text-gray-700">
                      Password <span className="text-gray-500 text-sm">(minimum 8 characters)</span>
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={newAdmin.password}
                      onChange={handleInputChange}
                      required
                      minLength={8}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-smd"
                    />
                  </div>
                  <div className="mt-2">
                    <label htmlFor="confirmPassword" className="block text-m font-medium text-gray-700">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={newAdmin.confirmPassword}
                      onChange={handleInputChange}
                      required
                      minLength={8}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-smd"
                    />
                  </div>
                  <div className="mt-2">
                    <label htmlFor="role" className="block text-m font-medium text-gray-700">
                      Role
                    </label>
                    <select
                      id="role"
                      name="role"
                      value={newAdmin.role}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-smd"
                    >
                      <option value="admin">Admin</option>
                      <option value="superadmin">Super Admin</option>
                    </select>
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-smd font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md text-smd font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ml-2 btn btn-primary"
                  >
                    {isLoading ? 'Creating...' : 'Create Admin'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Delete Admin</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-700">
                  Are you sure you want to delete the admin account for {selectedAdmin?.name}? This action cannot be undone.
                </p>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteModal(false);
                      setSelectedAdmin(null);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-smd font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteAdmin}
                    disabled={isLoading}
                    className="px-4 py-2 bg-red-600 text-white rounded-md text-smd font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ml-2 btn btn-primary"
                  >
                    {isLoading ? 'Deleting...' : 'Delete Admin'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminManagement; 